// ============================================================
// Conference Calls — data + honest provider status (PHASE_2K).
//
// "Conference Calls" is the VIDEO-conferencing layer (Daily.co provider). It
// is deliberately NOT "Daily Meetings" — Daily Meetings is the internal
// persisted meeting-record workflow at /operations/daily-meetings and is a
// separate thing. This module reads the REAL provider status from
// /api/square/daily/status (booleans + a mode enum; never a secret value) so
// the surface can show provider-ready vs INTEGRATION_DEFERRED /
// BACKEND_DEFERRED honestly. It never fabricates an active or scheduled call.
// ============================================================

import { commandCenterPath } from '../commandCenterRoutes'
import { useCallback, useEffect, useState } from 'react'
import { api, ApiError, isAuthError, type DailyStatus } from '../../../api/client'

export const CONFERENCE_CALLS_ROOT = commandCenterPath('communications/conference-calls')

// In-app Conference Room route (Daily custom "black studio" surface). Join
// navigates here; the scoped short-lived token is minted inside the room gate
// and held in memory only — never in this path (only the non-secret meetingId
// appears in the URL).
export const CONFERENCE_ROOMS_ROOT = commandCenterPath('communications/conference-rooms')
export const conferenceRoomPath = (meetingId: string) => `${CONFERENCE_ROOMS_ROOT}/${encodeURIComponent(meetingId)}`

export interface ConferenceCallStatusState {
  status: DailyStatus | null
  loading: boolean
  error: string | null
  authRequired: boolean
}

export function useConferenceCallStatus() {
  const [state, setState] = useState<ConferenceCallStatusState>({ status: null, loading: true, error: null, authRequired: false })
  const load = useCallback(async (signal?: AbortSignal) => {
    setState((s) => ({ ...s, loading: true, error: null }))
    try {
      const status = await api.commandCenter.meetings.status()
      if (signal?.aborted) return
      setState({ status, loading: false, error: null, authRequired: false })
    } catch (err) {
      if (signal?.aborted) return
      if (isAuthError(err)) { setState({ status: null, loading: false, error: null, authRequired: true }); return }
      const msg = err instanceof ApiError ? (err.message || err.code) : 'Could not reach the conference-call provider status.'
      setState({ status: null, loading: false, error: msg, authRequired: false })
    }
  }, [])
  useEffect(() => { const ac = new AbortController(); load(ac.signal); return () => ac.abort() }, [load])
  return { ...state, reload: () => load() }
}

// ---- PHASE_2M D4 (WIRE_DAILY_CONFERENCE_CALLS) ------------------------------
// The full create / join / copy / cancel flow against the REAL controlled-
// meeting backend (/api/square/daily/meetings*). Fail-closed: the create route
// itself refuses without DAILY_API_KEY; join tokens are scoped + short-lived;
// the browser NEVER sees the API key. Nothing here fabricates a room, a join
// URL, or an active call.

import type { Meeting } from '../../../api/client'

/** Operator-facing meeting modes (subset of the server's MEETING_MODES CHECK). */
export const CALL_MODES: Array<{ value: string; label: string }> = [
  { value: 'internal_operator', label: 'Internal operator call' },
  { value: 'client_demo', label: 'Client demo' },
  { value: 'business_meeting', label: 'Business meeting' },
  { value: 'team_handoff', label: 'Team handoff' },
  { value: 'training_session', label: 'Training session' },
  { value: 'onboarding', label: 'Onboarding' },
]

export interface ConferenceCallsState {
  meetings: Meeting[]
  providerConfigured: boolean | null
  loading: boolean
  error: string | null
  authRequired: boolean
}

export function useConferenceCalls() {
  const [state, setState] = useState<ConferenceCallsState>({ meetings: [], providerConfigured: null, loading: true, error: null, authRequired: false })
  const load = useCallback(async (signal?: AbortSignal) => {
    setState((s) => ({ ...s, loading: true, error: null }))
    try {
      const res = await api.commandCenter.meetings.list()
      if (signal?.aborted) return
      setState({ meetings: res.meetings || [], providerConfigured: res.provider_configured ?? null, loading: false, error: null, authRequired: false })
    } catch (err) {
      if (signal?.aborted) return
      if (isAuthError(err)) { setState({ meetings: [], providerConfigured: null, loading: false, error: null, authRequired: true }); return }
      const msg = err instanceof ApiError ? (err.message || err.code) : 'Could not load conference calls.'
      setState({ meetings: [], providerConfigured: null, loading: false, error: msg, authRequired: false })
    }
  }, [])
  useEffect(() => { const ac = new AbortController(); void load(ac.signal); return () => ac.abort() }, [load])
  return { ...state, reload: () => void load() }
}

export interface CreateCallInput {
  title: string
  meeting_mode: string
  duration_minutes?: number
  /** Optional REAL portal linking — stored on the meeting record (context_type/context_id). */
  portal_id?: string
  /** Optional REAL channel linking — stored on the meeting record. */
  channel_id?: string
}

/** Create a controlled Daily room. The server returns ok:false with an exact
 *  reason (provider_not_configured / approval-gated / provider error) — the
 *  caller surfaces it verbatim; no fake room ever appears. */
export async function createConferenceCall(input: CreateCallInput): Promise<{ ok: true; meeting: Meeting } | { ok: false; reason: string; message?: string; required_approval?: string | null }> {
  const context = input.portal_id
    ? { context_type: 'project_portal', context_id: input.portal_id }
    : input.channel_id
      ? { context_type: 'comms_channel', context_id: input.channel_id }
      : {}
  const res = await api.commandCenter.meetings.create({
    title: input.title,
    meeting_mode: input.meeting_mode,
    duration_minutes: input.duration_minutes,
    ...context,
  })
  if (res.ok && res.meeting) return { ok: true, meeting: res.meeting }
  return { ok: false, reason: (res as any).reason || 'create_failed', message: (res as any).message, required_approval: (res as any).required_approval ?? null }
}

// NOTE: the previous joinConferenceCall() built a room URL with the token in a
// query string and opened Daily's own interface in a new tab. That is replaced
// by the in-app Conference Room: Join navigates to conferenceRoomPath(meetingId)
// and the ConferenceRoomGate mints/holds the scoped token in memory only (never
// in a URL/query/storage/log/receipt/DB row). See ./conference-room.

export async function cancelConferenceCall(meeting_id: string, reason?: string): Promise<void> {
  await api.commandCenter.meetings.cancel(meeting_id, reason)
}

export const CALL_STATUS_META: Record<Meeting['status'], { label: string; accent: string }> = {
  created: { label: 'CREATED', accent: '#0891b2' },
  token_ready: { label: 'TOKEN READY', accent: '#0891b2' },
  active: { label: 'ACTIVE', accent: '#059669' },
  ended: { label: 'ENDED', accent: '#6a7480' },
  cancelled: { label: 'CANCELLED', accent: '#6a7480' },
  failed: { label: 'FAILED', accent: '#dc2626' },
  blocked: { label: 'BLOCKED', accent: '#b8862a' },
  receipted: { label: 'RECEIPTED', accent: '#059669' },
}

export type ConferenceTone = 'ready' | 'deferred' | 'unknown'

/** Honest provider label derived from the real Daily meeting_mode — no secrets. */
export function conferenceProviderStatus(status: DailyStatus | null): { label: string; detail: string; tone: ConferenceTone; badge: 'READY' | 'INTEGRATION_DEFERRED' | 'BACKEND_DEFERRED' | 'UNKNOWN' } {
  if (!status) return { label: 'Provider status unknown', detail: 'Sign in to the live cockpit to read the Daily.co provider status.', tone: 'unknown', badge: 'UNKNOWN' }
  switch (status.meeting_mode) {
    case 'ready_for_internal_test':
      return { label: 'Daily.co configured — ready for internal test', detail: 'DAILY_API_KEY is present and the schema is ready. Controlled rooms with scoped, short-lived join tokens; the browser never sees the API key.', tone: 'ready', badge: 'READY' }
    case 'blocked_missing_api_key':
      return { label: 'Daily.co not wired — DAILY_API_KEY missing', detail: 'The video-conferencing provider is not connected. Conference Calls stay INTEGRATION_DEFERRED until DAILY_API_KEY is configured server-side.', tone: 'deferred', badge: 'INTEGRATION_DEFERRED' }
    case 'blocked_migration_not_applied':
      return { label: 'Daily.co schema not migrated', detail: 'The controlled-call tables are not migrated. Conference Calls stay BACKEND_DEFERRED until the meeting-mode migration is applied.', tone: 'deferred', badge: 'BACKEND_DEFERRED' }
    default:
      return { label: 'Provider status unknown', detail: 'The provider reported an unrecognized state.', tone: 'unknown', badge: 'UNKNOWN' }
  }
}
